import { Dispatch, useCallback, useState } from 'react';
import debounce from 'lodash.debounce';
import { useDispatch } from 'react-redux';
import { useField, useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { requirementsNpaFindSelectOptionsByQuery } from '../../../KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { ISelectOption, SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { IRequirementNpaFilterForm } from '../interfaces/IRequirementNpaFilterForm';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { PassportGroupPassportTypeEnum } from '../enums/PassportGroupPassportTypeEnum';
import styles from './EventSubjectGroupCondition.module.scss';
import { RequirementNpaModal } from './RequirementNpaModal/RequirementNpaModal';
import { IRequirementNpa } from '../../../APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { IRequirementStructuralUnitFilterForm } from '../interfaces/IRequirementStructuralUnitFilterForm';
import { IRequirementsMandatoryNew } from '../../../APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';
import collapseStyles from '../../../KnmPackage/MainModule/components/KnmCollapse.module.scss';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { dictionariesGetDocTargetSelectOptionsByQuery } from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import { IGroupEventSubjectConditionsForm } from '../interfaces/IGroupEventSubjectConditionsForm';

interface IEventSubjectGroupConditionProps {
  name: string;
  category: IGroupConditionForm['category'];
}

export const EventSubjectGroupCondition = ({ name, category }: IEventSubjectGroupConditionProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const requirementsFieldName = `${name}.requirements`;
  const form = useForm();
  const requirementsValue: IGroupEventSubjectConditionsForm['requirements'] =
    useField(requirementsFieldName, {
      subscription: { value: true },
    }).input.value || [];
  const { push, remove } = form.mutators;
  const [selectedNpa, setSelectedNpa] = useState<IRequirementNpa | undefined>();
  const [selectedNpaIndex, setSelectedNpaIndex] = useState(0);

  const isErknm = category === PassportGroupPassportTypeEnum.ERKNM;
  const isPm = category === PassportGroupPassportTypeEnum.PM;
  const isDecision = category === PassportGroupPassportTypeEnum.DECISION_PASSPORT;
  const isErp = category === PassportGroupPassportTypeEnum.ERP;

  const npaLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      // Поиск происходит при вводе поискового запроса более двух символов
      if (inputValue.length < 3) {
        callback([]);
      } else {
        dispatch(requirementsNpaFindSelectOptionsByQuery(inputValue, callback));
      }
    }, 1000),
    [dispatch],
  );

  const handleAddNpa = useCallback(() => {
    const newValue: IRequirementNpaFilterForm = {};

    push && push(requirementsFieldName, newValue);
  }, [push, requirementsFieldName]);

  const handleSelectNpa = useCallback(
    (value: ISelectOption, index: number) => {
      form.change(`${requirementsFieldName}[${index}].npa`, value.value);
    },
    [form, requirementsFieldName],
  );

  const handleAddStructuralUnit = useCallback(
    (npa: IRequirementNpa | undefined, index: number) => {
      setSelectedNpa(npa);
      setSelectedNpaIndex(index);
    },
    [setSelectedNpa],
  );

  const docTargetLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      dispatch(dictionariesGetDocTargetSelectOptionsByQuery(inputValue, callback));
    }, 1000),
    [dispatch],
  );

  const handleSubmitAddStructuralUnit = useCallback(
    (values: IRequirementStructuralUnitFilterForm[]) => {
      setSelectedNpa(undefined);

      values.forEach(item => push(`${requirementsFieldName}[${selectedNpaIndex}].structuralUnits`, item));
    },
    [setSelectedNpa, form, requirementsFieldName, selectedNpaIndex],
  );

  if (isErp) {
    return null;
  }

  return (
    <>
      <KnmCollapse title="Предмет мероприятия">
        <>
          <FieldBlock
            label={
              isDecision
                ? 'Выявленные нарушения обязательных требований'
                : 'Обязательные требования, подлежащие проверке'
            }
            margin="bottom"
            button={
              <Button
                className="shared-btn-add"
                type="button"
                variant="link"
                color="primary"
                text="Добавить НПА"
                onClick={handleAddNpa}
                disabled={!!requirementsValue?.length}
              />
            }
          >
            <FieldArray name={requirementsFieldName}>
              {({ fields }) =>
                fields.map((item, index) => {
                  const fieldValue: IRequirementNpaFilterForm = fields.value[index];

                  if (fieldValue.npa) {
                    return (
                      <KnmCollapse
                        title={
                          <>
                            <span className={styles.CollapseTitleGrey}>НПА №</span>
                            {fieldValue.npa.number}
                            <span className={styles.CollapseTitleGrey}>от</span>
                            {fieldValue.npa.acceptanceDate}
                          </>
                        }
                        theme="fields"
                        key={item}
                        onClose={() => remove(requirementsFieldName, index)}
                      >
                        <>
                          <div className={styles.NpaName}>{fieldValue.npa.title}</div>
                          <div className={styles.StructureUnitTitle}>
                            <span>Структурные единицы</span>
                            <Button
                              className="shared-btn-add"
                              type="button"
                              variant="link"
                              color="primary"
                              text="Добавить"
                              onClick={() => handleAddStructuralUnit(fieldValue.npa, index)}
                            />
                          </div>
                          <FieldArray name={`${item}.structuralUnits`}>
                            {({ fields: suFields }) =>
                              suFields.map((suItem, suIndex) => {
                                const suFieldValue: IRequirementStructuralUnitFilterForm = suFields.value[suIndex];

                                return (
                                  <div key={suItem}>
                                    <div className={styles.SuItemWrapper}>
                                      <div>{suIndex + 1}</div>
                                      <div>{suFieldValue.structuralUnit.title}</div>
                                      <CloseButton
                                        onClick={() =>
                                          remove(`${requirementsFieldName}[${index}].structuralUnits`, suIndex)
                                        }
                                      />
                                    </div>
                                    <FieldArray name={`${suItem}.mandatory`}>
                                      {({ fields: mandatoryFields }) =>
                                        mandatoryFields.map((mandatoryItem, mandatoryIndex) => {
                                          const mandatoryFieldValue: IRequirementsMandatoryNew =
                                            mandatoryFields.value[mandatoryIndex];

                                          return (
                                            <div key={mandatoryItem} className={styles.MandatoryBlock}>
                                              <div className={styles.MandatoryBlockHeader}>
                                                Содержание требования
                                                <button
                                                  type="button"
                                                  className={collapseStyles.DeleteButton}
                                                  onClick={() => remove(`${suItem}.mandatory`, mandatoryIndex)}
                                                >
                                                  <span className={collapseStyles.DeleteButtonIcon} />
                                                </button>
                                              </div>
                                              <div className={styles.MandatoryBlockBody}>
                                                {mandatoryFieldValue.title}
                                              </div>
                                            </div>
                                          );
                                        })
                                      }
                                    </FieldArray>
                                  </div>
                                );
                              })
                            }
                          </FieldArray>
                        </>
                      </KnmCollapse>
                    );
                  }

                  return (
                    <FieldBlock label="НПА" key={index} margin="bottom">
                      <div className="shared-field-row">
                        <SelectInput
                          className="shared-deleting-row"
                          isAsync
                          isSearchable
                          loadOptions={npaLoadOptions}
                          onChange={value => handleSelectNpa(value, index)}
                        />
                        <CloseButton
                          className="shared-deleting-row-btn"
                          onClick={() => remove(requirementsFieldName, index)}
                        />
                      </div>
                    </FieldBlock>
                  );
                })
              }
            </FieldArray>
          </FieldBlock>
          {(isErknm || isPm) && (
            <FieldBlock label="Разрешительные и иные документы" margin="bottom">
              <SelectInputField
                name={`${name}.docTargets`}
                isClearable
                isAsync
                isSearchable
                loadOptions={docTargetLoadOptions}
                isMulti
                cacheOptions={false}
              />
            </FieldBlock>
          )}
        </>
      </KnmCollapse>
      <RequirementNpaModal
        selectedNpa={selectedNpa}
        onClose={() => setSelectedNpa(undefined)}
        onSubmit={handleSubmitAddStructuralUnit}
        withMandatory={!isErknm}
      />
    </>
  );
};
